<template>
  <Modal
    :show="showPermissionError"
    width="700px"
    :action-text="null"
    :cancel-text="null"
    @close="setPermissionsError(null)"
  >
    <template #title>
      {{ config.atsName }} API Key Permissions
    </template>

    <template #content>
      <div>
        Your API key does not have the required permissions to begin our integration.
        Please review
        <a
          href="https://support.greenhouse.io/hc/en-us/articles/360058701612-Datapeople"
          target="_blank"
          rel="noopener noreferrer"
        >
          this article
        </a>
        for the required permissions.
      </div>

      <template v-if="!permissionError.tokenValid">
        <div
          class="mt-4 permission-line"
        >
          The key you provided is not a valid Greenhouse Harvest API key.
          Please check your key and try again.
        </div>
      </template>

      <template v-else-if="!permissionError.permissionsSufficient">
        <div class="mt-2 mb-3">
          Please add the missing permissions, listed with an
          <v-icon
            size="12"
            color="error"
            icon="$close"
          />
          below, and submit your key again:
        </div>

        <div class="d-flex">
          <div
            v-for="{ id, column } in permissionColumns"
            :key="id"
            class="permission-column"
          >
            <div
              v-for="{ name, permissionList } in column"
              :key="name"
              class="mt-2"
            >
              <h5 class="mb-2">
                {{ name }}
              </h5>
              <div
                v-for="{ permissionName, valid } in permissionList"
                :key="permissionName"
                :class="{ 'permission-line': true, valid }"
              >
                {{ permissionName }}
                <v-icon
                  size="12"
                  :color="valid ? 'success' : 'error'"
                  :icon="valid ? '$check' : '$close'"
                />
              </div>
            </div>
          </div>
        </div>
      </template>
    </template>
  </Modal>
</template>

<script>
import { useSettingsConfig } from '@/stores/settings/config';
import { useSettingsIntegrations } from '@/stores/settings/integrations';
import Modal from '@/components/shared/Modal.vue';
import { mapActions, mapState } from 'pinia';

const permissionGroupLabels = [
  ['applications', 'Applications'],
  ['candidates', 'Candidates'],
  ['departments', 'Departments'],
  ['jobs', 'Jobs'],
  ['job_posts', 'Job Posts'],
  ['job_stages', 'Job Stages'],
  ['sources', 'Sources'],
  ['users', 'Users'],
  ['eeoc', 'EEOC'],
  ['user_roles', 'User Roles'],
  ['job_openings', 'Job Openings'],
  ['custom_fields', 'Custom Fields'],
  ['acknowledgements-settings', 'Acknowledgements'],
];

const permissionGroups = permissionGroupLabels.reduce((res, [id, label], index) => ({
  ...res,
  [id]: { id, label, index },
}), {});

const permissionLabels = {
  applications: {
    RETRIEVE: 'GET: Retrieve Application',
    LIST: 'GET: List Applications',
  },
  candidates: {
    RETRIEVE: 'GET: Retrieve Candidate',
    LIST: 'GET: List Candidates',
  },
  departments: {
    RETRIEVE: 'GET: Retrieve Department',
    LIST: 'GET: List Departments',
  },
  jobs: {
    RETRIEVE: 'GET: Retrieve Job',
    LIST: 'GET: List Jobs',
    HIRING_TEAM: 'GET: Hiring team',
    STAGES: 'GET: List Job Stages for Job',
    OPENINGS: 'GET: List Openings For a Job',
    JOB_POSTS: 'GET: List Job Posts for Job',
  },
  job_posts: {
    LIST: 'GET: List Job Posts',
    RETRIEVE: 'GET: Retrieve Job Post for Job',
    PATCH: 'PATCH: Update job posts',
  },
  job_stages: {
    RETRIEVE: 'GET: Retrieve Job Stage',
    LIST: 'GET: List Job Stages',
  },
  sources: {
    LIST: 'GET: List Sources',
  },
  users: {
    RETRIEVE: 'GET: Retrieve User',
    LIST: 'GET: List Users',
    'PERMISSIONS/JOBS': 'GET: List User Job Permissions',
    'PERMISSIONS/FUTURE_JOBS': 'GET: List User Future Job Permissions',
  },
  eeoc: {
    RETRIEVE: 'GET: Retrieve EEOC Data for Application',
    LIST: 'GET: List EEOC',
  },
  user_roles: {
    LIST: 'GET: List User Roles',
  },
  custom_fields: {
    LIST: 'GET: Get custom fields',
  },
};

export default {
  name: 'PermissionErrorModal',
  components: { Modal },
  computed: {
    ...mapState(useSettingsIntegrations, ['showPermissionError', 'permissionError']),
    ...mapState(useSettingsConfig, ['config']),

    permissions() {
      const { permissionsDetail } = this.permissionError || {};
      return Object
        .keys(permissionsDetail)
        .sort((a, b) => permissionGroups[a]?.index - permissionGroups[b]?.index)
        .map((name) => ({
          name: permissionGroups[name]?.label || name,
          permissionList: Object.keys(permissionsDetail[name]).map((permissionName) => ({
            permissionName: permissionLabels[name][permissionName] || permissionName,
            valid: permissionsDetail[name][permissionName],
          })),
        }));
    },

    permissionColumns() {
      return [
        { id: 'left', column: this.permissions.filter((_, i) => i < 5) },
        { id: 'right', column: this.permissions.filter((_, i) => i >= 5) },
      ];
    },
  },

  methods: {
    ...mapActions(useSettingsIntegrations, ['setPermissionsError']),
  },
};
</script>

<style scoped lang="less">
.permission-line {
  color: @basic-red;

  &.valid {
    color: @basic-green;
  }
}

.permission-column {
  width: 50%;
}
</style>
